import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';

import { AfterViewInit, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Params } from '@angular/router';
import { Company, DynamicField, OptionsInput } from '@cargos/sprintpay-models';
import { AppFeaturesShipmentOrSearchForm, DynamicFieldPersistent } from '../../../pages/form-app/models/app-form';
import { FeaturesConfig, FeaturesConfigShipmentForm } from '../../models/organization-data.model';
import { BackgroundImageUploadComponent } from '../background-image-upload/background-image-upload.component';
import { DynamicFieldFormComponent } from '../dynamic-field-form/dynamic-field-form.component';
import {
    DynamicFieldBaseName,
    DynamicFieldForm,
    DynamicFieldPersistentNames,
    DynamicFieldPersistentNamesThatCannotBeDisabled,
    DynamicFieldType,
    DynamicFieldValidation,
} from '../dynamic-field-form/models/dynamic-field-form';
import { InputCustomizationComponent } from '../input-customization/input-customization.component';

@Component({
    selector: 'app-shipment-form',
    imports: [
        FormsModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatExpansionModule,
        DynamicFieldFormComponent,
        CdkDropList,
        CdkDrag,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatProgressSpinnerModule,
        BackgroundImageUploadComponent,
        InputCustomizationComponent,
    ],
    templateUrl: './shipment-form.component.html',
    styleUrl: './shipment-form.component.scss',
})
export class ShipmentFormComponent implements OnChanges, AfterViewInit {
    @Input() isEdition: boolean = false;
    @Input() formGroup: FormGroup<AppFeaturesShipmentOrSearchForm>;
    @Input() removeShadowControl?: FormControl<boolean | null>;
    @Input() set features(features: FeaturesConfig | undefined) {
        this.featuresConfig = features;
        this.patchDynamicFields(features);
        this.orgBackgroundImages =
            (features?.shipmentForm?.backgroundImages as { name?: string | null }[]) ||
            (features?.searchForm?.backgroundImages as { name?: string | null }[]);
    }

    public featuresConfig: FeaturesConfig | undefined;
    public orgBackgroundImages: { name?: string | null }[] = [];
    public persistentFields: DynamicFieldPersistent[] = [];
    public extraFieldNameRegex: RegExp = new RegExp(`^${DynamicFieldBaseName}\\d+$`);
    public dynamicFieldsLimit: number = 4;
    public form: FormGroup<AppFeaturesShipmentOrSearchForm>;

    get dynamicFields(): FormArray<FormGroup<DynamicFieldForm>> {
        return this.form?.get('dynamicFields') as FormArray<FormGroup<DynamicFieldForm>>;
    }

    get showAddDynamicFieldButton(): boolean {
        return this.getExtraFieldsControls().length < this.dynamicFieldsLimit;
    }

    get backgroundImages():
        | FormArray<
              FormGroup<{
                  name: FormControl<string | null>;
              }>
          >
        | undefined {
        return this.form?.controls['backgroundImages'];
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.form = this.formGroup;
            if (this.dynamicFields.length === 0) {
                this.setPersistentFields();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['formGroup'] && changes['formGroup'].currentValue) {
            this.form = changes['formGroup'].currentValue;
        }
    }

    setPersistentFields(): void {
        this.persistentFields = [
            {
                name: 'awb',
                label: 'AWB',
                placeholder: 'AWB',
                canDisabled: false,
                isRequired: true,
                fieldType: DynamicFieldType.INPUT,
                selectedValidators: ['required'],
            },
            {
                name: 'amount',
                label: 'Amount',
                placeholder: 'Amount',
                canDisabled: false,
                isRequired: true,
                fieldType: DynamicFieldType.INPUT,
                selectedValidators: ['required'],
            },
            {
                name: 'hawbId',
                label: 'HAWB',
                placeholder: 'HAWB',
                canDisabled: true,
                isRequired: false,
                fieldType: DynamicFieldType.INPUT,
            },
        ];
        this.addPersistentField();
    }

    addPersistentField(): void {
        this.persistentFields.forEach((field) =>
            this.addDynamicField(<DynamicField>{
                name: field.name,
                label: field.label,
                placeholder: field.placeholder,
                isDisabled: field.name === 'hawbId',
                isRequired: field.isRequired,
                fieldType: field.fieldType,
                options: field.options,
            })
        );
    }

    isPersistentField(dynamicFieldName: string | null | undefined): boolean {
        return DynamicFieldPersistentNames.includes(dynamicFieldName || '');
    }

    showDeleteButton(dynamicFieldForm: FormGroup<DynamicFieldForm>): boolean {
        return !this.isPersistentField(dynamicFieldForm.controls['name'].value);
    }

    showDisabledToggle(dynamicFieldForm: FormGroup<DynamicFieldForm>): boolean {
        return this.canDisableField(dynamicFieldForm.controls['name'].value);
    }

    canDisableField(fieldName: string | null | undefined): boolean {
        if (!fieldName) {
            return false;
        }

        const persistentField = this.persistentFields.find((field) => field.name === fieldName);
        if (persistentField) {
            return persistentField.canDisabled;
        }

        return !DynamicFieldPersistentNamesThatCannotBeDisabled.includes(fieldName);
    }

    deleteDynamicField(index: number): void {
        this.dynamicFields.removeAt(index);
        this.assignNamesExtraFields();
    }

    assignNamesExtraFields(): void {
        const extraFields: Array<FormGroup<DynamicFieldForm>> = this.getExtraFieldsControls();
        extraFields.forEach((ctrl: FormGroup<DynamicFieldForm>, index: number) => {
            ctrl.get('name')?.setValue(`${DynamicFieldBaseName}${index + 1}`);
        });
    }

    getExtraFieldsControls(): Array<FormGroup<DynamicFieldForm>> {
        return this.dynamicFields.controls.filter((ctrl: FormGroup<DynamicFieldForm>) => {
            const name: string = ctrl.get('name')?.value as string;
            return name && this.extraFieldNameRegex.test(name);
        });
    }

    getNextExtraFieldName(): string {
        const extraFields: Array<FormGroup<DynamicFieldForm>> = this.getExtraFieldsControls();
        return `${DynamicFieldBaseName}${extraFields.length + 1}`;
    }

    addDynamicField(field?: DynamicField): void {
        const isPersistent: boolean = this.isPersistentField(field?.name);
        const persistentField: DynamicFieldPersistent | undefined = isPersistent
            ? this.persistentFields.find((persistent) => persistent.name === field?.name)
            : undefined;
        const nameValidators: ValidatorFn[] = [Validators.required];
        const typeValidators: ValidatorFn[] = [Validators.required];
        let labelValidators: ValidatorFn[] = [];
        let placeholderValidators: ValidatorFn[] = [];
        if (isPersistent) {
            if (persistentField && persistentField.canDisabled) {
                labelValidators = [];
                placeholderValidators = [];
            } else {
                labelValidators = [Validators.required];
                placeholderValidators = [Validators.required];
            }
        } else {
            labelValidators = [Validators.required];
            placeholderValidators = [Validators.required];
        }

        this.dynamicFields?.push(
            new FormGroup<DynamicFieldForm>({
                name: new FormControl<string>(
                    persistentField ? field?.name || '' : this.getNextExtraFieldName(),
                    nameValidators
                ),
                label: new FormControl<string>(field?.label || '', labelValidators.length ? labelValidators : null),
                placeholder: new FormControl<string>(
                    field?.placeholder || '',
                    placeholderValidators.length ? placeholderValidators : null
                ),
                isDisabled: new FormControl<boolean>(field?.isDisabled || false),
                fieldType: new FormControl<string>(field?.fieldType || DynamicFieldType.INPUT, typeValidators),
                options: new FormArray<FormControl<string>>(
                    field?.options
                        ? field.options.map(
                              (opt) =>
                                  new FormControl<string>(opt.value as string, {
                                      nonNullable: true,
                                  })
                          )
                        : []
                ),
                selectedValidators: new FormControl<string[] | null>(
                    field?.validations && field.validations.length
                        ? field.validations.flatMap((validation) => validation.name as string)
                        : isPersistent
                          ? persistentField?.selectedValidators || null
                          : null
                ),
                validations: new FormArray<FormGroup<DynamicFieldValidation>>(
                    field?.validations
                        ? field.validations.map(
                              (validation) =>
                                  new FormGroup<DynamicFieldValidation>({
                                      id: new FormControl<number | null>(validation.id ?? null),
                                      name: new FormControl<string | null>(validation.name ?? null),
                                      value: new FormControl<string | RegExp | string[] | RegExp[] | null>(
                                          validation.value ?? null
                                      ),
                                      message: new FormControl<string | null>(validation.message ?? null),
                                      title: new FormControl<string | null>(validation.title ?? null),
                                      type: new FormControl<string | null>(validation.type ?? null),
                                      apiValidator: new FormControl<string | null>(validation.apiValidator ?? null),
                                      params: new FormControl<Params[] | null>(validation.params ?? null),
                                      typeRequest: new FormControl<string | null>(validation.typeRequest ?? null),
                                  })
                          )
                        : []
                ),
                id: new FormControl<number | null>(field?.id || null),
                section: new FormControl<number | null>(field?.section || null),
                company: new FormControl<Company | null>(field?.company || null),
                emailDomain: new FormControl<string | null>(field?.emailDomain || null),
                isReplacement: new FormControl<boolean | null>(field?.isReplacement || null),
                isHidden: new FormControl<boolean | null>(field?.isHidden || null),
                subLabel: new FormControl<string | null>(field?.subLabel || null),
                tooltipTitle: new FormControl<string | null>(field?.tooltipTitle || null),
                tooltipMessage: new FormControl<string | null>(field?.tooltipMessage || null),
                hint: new FormControl<string | null>(field?.hint || null),
                value: new FormControl<string | null>(field?.value || null),
                isDynamic: new FormControl<boolean | null>(field?.isDynamic || null),
                isValueMappingEnabled: new FormControl<boolean | null>(field?.isValueMappingEnabled || null),
                mappedFieldName: new FormControl<string | null>(field?.mappedFieldName || null),
            })
        );
    }

    convertToDynamicFieldOptionsForm(options: OptionsInput[] | string[] | undefined): OptionsInput[] {
        return options
            ? options.map((opt) => ({
                  value: typeof opt === 'string' ? opt : opt.value,
                  displayValue: typeof opt === 'string' ? opt : (opt.displayValue ?? opt.value ?? ''),
                  ...(typeof opt === 'object' && 'criteria' in opt && opt.criteria ? { criteria: opt.criteria } : {}),
              }))
            : [];
    }

    patchDynamicFields(features: FeaturesConfig | undefined): void {
        const orgDynamicFields = features?.shipmentForm?.dynamicFields || [];
        const dynamicFieldsCtrl = this.dynamicFields;
        dynamicFieldsCtrl?.clear();

        if (features && orgDynamicFields.length) {
            orgDynamicFields.forEach((field: DynamicField) => {
                const persistentField = this.persistentFields.find((pf) => pf.name === field.name);
                if (persistentField) {
                    this.addDynamicField({
                        ...persistentField,
                        ...field,
                        options: this.convertToDynamicFieldOptionsForm(field.options),
                        validations: field.validations ?? [],
                    });
                } else {
                    this.addDynamicField({
                        ...field,
                        options: this.convertToDynamicFieldOptionsForm(field.options),
                    });
                }
            });
        }
        if (features && features.shipmentForm) {
            const shipmentForm: FeaturesConfigShipmentForm = features.shipmentForm as FeaturesConfigShipmentForm;
            const persistentNames: string[] = DynamicFieldPersistentNames;
            persistentNames.forEach((name) => {
                if (shipmentForm[name] !== undefined && !orgDynamicFields.some((field) => field.name === name)) {
                    const persistentField = this.persistentFields.find((pf) => pf.name === name);
                    if (persistentField) {
                        let validations =
                            persistentField.selectedValidators && (name === 'awb' || name === 'amount')
                                ? persistentField.selectedValidators.map((vName) => ({
                                      id: 1,
                                      name: vName,
                                      value: vName === 'required' ? true : undefined,
                                      message: vName === 'required' ? 'This field is required' : undefined,
                                      title: null,
                                      type: 'VALIDATION',
                                      apiValidator: null,
                                      params: null,
                                      typeRequest: null,
                                  }))
                                : [];

                        if (shipmentForm[name]?.validations) {
                            validations = shipmentForm[name].validations;
                        }

                        this.addDynamicField({
                            ...persistentField,
                            validations,
                            ...shipmentForm[name],
                        });
                    }
                }
            });
        }
        this.addMissingPersistentDynamicFields(features, orgDynamicFields);
    }

    addMissingPersistentDynamicFields(features: FeaturesConfig | undefined, orgDynamicFields: DynamicField[]): void {
        this.persistentFields.forEach((persistentField) => {
            const exists =
                orgDynamicFields.some((field) => field.name === persistentField.name) ||
                (features?.shipmentForm && features.shipmentForm[persistentField.name] !== undefined);
            if (!exists) {
                this.addDynamicField({
                    name: persistentField.name,
                    label: persistentField.label,
                    placeholder: persistentField.placeholder,
                    fieldType: persistentField.fieldType,
                    options: this.convertToDynamicFieldOptionsForm(persistentField.options),
                    validations: persistentField.selectedValidators
                        ? persistentField.selectedValidators.map((name) => ({ name }))
                        : [],
                } as DynamicField);
            }
        });
    }

    drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.dynamicFields.controls, event.previousIndex, event.currentIndex);
        this.dynamicFields.updateValueAndValidity();
    }
}
